import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Wallet, Landmark, CreditCard, Save, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const empty = {
  cash_enabled: true,
  bank_transfer_enabled: true,
  stripe_enabled: false,
  paypal_enabled: false,
  bank_name: "",
  iban: "",
  bic_swift: "",
  bank_beneficiary: "",
  paypal_client_id: "",
  paypal_secret: "",
  paypal_mode: "sandbox",
  // 🆕 22/07/2026 — Ricarico commissione Stripe sul cliente
  stripe_fee_percent: 1.5,
  stripe_fee_fixed: 0.25,
  stripe_charge_fee_to_client: true,
  // Etichette/descrizioni mostrate al cliente (override dei testi default)
  cash_label: "",
  cash_description: "",
  bank_transfer_label: "",
  bank_transfer_description: "",
  stripe_label: "",
  stripe_description: "",
  paypal_label: "",
  paypal_description: "",
};

const ClientLabelEditor = ({ method, label, description, defaultLabel, defaultDescription, onChange }) => (
  <div className="ml-13 mt-3 rounded-md border border-slate-200 bg-slate-50/40 p-3">
    <p className="text-[11px] uppercase tracking-wider font-semibold text-slate-600 mb-2">
      💬 Testi mostrati al cliente nella pagina pubblica offerta
    </p>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <div>
        <Label className="text-xs">Titolo metodo</Label>
        <Input
          data-testid={`${method}-client-label`}
          value={label || ""}
          onChange={(e) => onChange({ [`${method}_label`]: e.target.value })}
          placeholder={defaultLabel}
        />
        <p className="text-[10px] text-slate-500 mt-0.5">Default se vuoto: <em>{defaultLabel}</em></p>
      </div>
      <div>
        <Label className="text-xs">Descrizione (riga sotto al titolo)</Label>
        <Input
          data-testid={`${method}-client-description`}
          value={description || ""}
          onChange={(e) => onChange({ [`${method}_description`]: e.target.value })}
          placeholder={defaultDescription}
        />
        <p className="text-[10px] text-slate-500 mt-0.5">Default se vuoto: <em>{defaultDescription}</em></p>
      </div>
    </div>
  </div>
);

export default function AgencyPaymentSettings() {
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/agency/payment-settings")
      .then((r) => setForm({ ...empty, ...r.data }))
      .catch((e) => toast.error(formatApiError(e)));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/agency/payment-settings", form);
      toast.success("Configurazione pagamenti salvata");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));
  const ibanValid = !form.iban || /^[A-Z]{2}\d{2}[A-Z0-9]{1,30}$/.test(form.iban.replace(/\s/g, "").toUpperCase());

  return (
    <div className="space-y-6 animate-fade-up" data-testid="payment-settings-page">
      <header>
        <p className="text-xs uppercase tracking-[0.2em] text-blue-600 mb-2">Configurazione</p>
        <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">Metodi di pagamento</h1>
        <p className="text-slate-500 mt-1">Attiva o disattiva i metodi disponibili nell&apos;area cliente. I dati bancari e PayPal sono usati solo se i relativi metodi sono attivi.</p>
      </header>

      <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-6">

        {/* Cash */}
        <div className="flex items-center justify-between border-b border-slate-100 pb-5">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-md bg-amber-100 text-amber-700 flex items-center justify-center">
              <Wallet className="h-5 w-5" />
            </div>
            <div>
              <p className="font-semibold">Contanti in agenzia</p>
              <p className="text-xs text-slate-500">Il cliente prenota, paga allo sportello. Confermi manualmente l&apos;incasso.</p>
            </div>
          </div>
          <Switch data-testid="cash-toggle" checked={form.cash_enabled} onCheckedChange={(v) => update({ cash_enabled: v })} />
        </div>
        {form.cash_enabled && (
          <ClientLabelEditor
            method="cash"
            label={form.cash_label}
            description={form.cash_description}
            defaultLabel="Contanti in agenzia"
            defaultDescription="Vieni a pagare in contanti presso la nostra sede."
            onChange={update}
          />
        )}

        {/* Bank transfer */}
        <div className="space-y-3 border-b border-slate-100 pb-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-md bg-blue-100 text-blue-700 flex items-center justify-center">
                <Landmark className="h-5 w-5" />
              </div>
              <div>
                <p className="font-semibold">Bonifico bancario</p>
                <p className="text-xs text-slate-500">Mostra IBAN + causale al cliente. Conferma manualmente l&apos;accredito.</p>
              </div>
            </div>
            <Switch data-testid="bank-toggle" checked={form.bank_transfer_enabled} onCheckedChange={(v) => update({ bank_transfer_enabled: v })} />
          </div>
          {form.bank_transfer_enabled && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3 ml-13">
              <div className="col-span-2">
                <Label>Intestatario conto</Label>
                <Input data-testid="bank-beneficiary" value={form.bank_beneficiary || ""} onChange={(e) => update({ bank_beneficiary: e.target.value })} placeholder="Es. Studio Servizi Globali Srl" />
              </div>
              <div>
                <Label>Banca</Label>
                <Input data-testid="bank-name" value={form.bank_name || ""} onChange={(e) => update({ bank_name: e.target.value })} placeholder="Es. Intesa Sanpaolo" />
              </div>
              <div>
                <Label>BIC / SWIFT</Label>
                <Input data-testid="bank-bic" value={form.bic_swift || ""} onChange={(e) => update({ bic_swift: e.target.value })} placeholder="Es. BCITITMM" />
              </div>
              <div className="col-span-2">
                <Label>IBAN</Label>
                <Input data-testid="bank-iban" value={form.iban || ""} onChange={(e) => update({ iban: e.target.value.toUpperCase() })} placeholder="IT60 X054 2811 1010 0000 0123 456" className={`font-mono ${!ibanValid ? "border-red-300" : ""}`} />
                {!ibanValid && <p className="text-[11px] text-red-600 mt-1">IBAN non valido (formato atteso: 2 lettere + 2 cifre + 1-30 caratteri).</p>}
              </div>
            </div>
          )}
          {form.bank_transfer_enabled && (
            <ClientLabelEditor
              method="bank_transfer"
              label={form.bank_transfer_label}
              description={form.bank_transfer_description}
              defaultLabel="Bonifico bancario"
              defaultDescription="Effettua un bonifico verso questo IBAN. Una volta ricevuto, l'agenzia procederà all'emissione."
              onChange={update}
            />
          )}
        </div>

        {/* Stripe */}
        <div className="flex items-center justify-between border-b border-slate-100 pb-5">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-md bg-indigo-100 text-indigo-700 flex items-center justify-center">
              <CreditCard className="h-5 w-5" />
            </div>
            <div>
              <p className="font-semibold">Carta di credito (Stripe)</p>
              <p className="text-xs text-slate-500">Visa, Mastercard, Amex. La chiave Stripe è già configurata a livello server (test mode pronto, prod a deploy).</p>
            </div>
          </div>
          <Switch data-testid="stripe-toggle" checked={form.stripe_enabled} onCheckedChange={(v) => update({ stripe_enabled: v })} />
        </div>
        {form.stripe_enabled && (
          <>
            {/* 🆕 22/07/2026 — Configurazione ricarico commissione Stripe */}
            <div className="ml-13 mt-3 rounded-md border border-indigo-200 bg-indigo-50/40 p-3 space-y-3">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="text-[11px] uppercase tracking-wider font-semibold text-indigo-700">💳 Ricarico commissione Stripe sul cliente</p>
                  <p className="text-[11px] text-slate-600 mt-0.5">
                    Se attivo, la commissione Stripe viene <strong>caricata sul cliente</strong>: incassi <strong>esattamente</strong> il premio della polizza.
                  </p>
                </div>
                <Switch
                  data-testid="stripe-charge-fee-toggle"
                  checked={form.stripe_charge_fee_to_client}
                  onCheckedChange={(v) => update({ stripe_charge_fee_to_client: v })}
                />
              </div>
              {form.stripe_charge_fee_to_client && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Percentuale (%)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      max="99.99"
                      data-testid="stripe-fee-percent"
                      value={form.stripe_fee_percent ?? ""}
                      onChange={(e) => update({ stripe_fee_percent: e.target.value === "" ? null : Number(e.target.value) })}
                      placeholder="1.5"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Fisso (€)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      data-testid="stripe-fee-fixed"
                      value={form.stripe_fee_fixed ?? ""}
                      onChange={(e) => update({ stripe_fee_fixed: e.target.value === "" ? null : Number(e.target.value) })}
                      placeholder="0.25"
                    />
                  </div>
                </div>
              )}
              {form.stripe_charge_fee_to_client && (form.stripe_fee_percent > 0 || form.stripe_fee_fixed > 0) && (
                <div className="rounded bg-white border border-indigo-200 p-2.5 text-xs">
                  <p className="font-semibold text-slate-700 mb-1">🧮 Simulatore</p>
                  {(() => {
                    // Formula: gross = (net + fixed) / (1 - pct/100)
                    const testNet = 500;
                    const pct = Number(form.stripe_fee_percent || 0);
                    const fixed = Number(form.stripe_fee_fixed || 0);
                    const denom = 1 - pct / 100;
                    const gross = denom > 0 ? (testNet + fixed) / denom : null;
                    const feeAmount = gross != null ? gross - testNet : null;
                    return gross != null ? (
                      <div className="tabular-nums text-slate-700 space-y-0.5">
                        <div>Premio polizza (netto agenzia): <strong>€ {testNet.toFixed(2)}</strong></div>
                        <div>Ricarico commissione Stripe: <strong className="text-indigo-700">+ € {feeAmount.toFixed(2)}</strong></div>
                        <div className="pt-1 border-t border-slate-100">Cliente paga: <strong className="text-emerald-700 text-sm">€ {gross.toFixed(2)}</strong></div>
                      </div>
                    ) : <span className="text-rose-700">Percentuale non valida.</span>;
                  })()}
                </div>
              )}
              <p className="text-[10px] text-slate-500">
                📌 Tariffa standard Stripe carte europee (SEPA): <strong>1,5% + € 0,25</strong>. Carte non-SEPA: 2,5% + € 0,25.
                Puoi personalizzare in base al piano Stripe che hai negoziato.
              </p>
            </div>
            <ClientLabelEditor
              method="stripe"
              label={form.stripe_label}
              description={form.stripe_description}
              defaultLabel="Paga ora con carta"
              defaultDescription="Pagamento sicuro Stripe"
              onChange={update}
            />
          </>
        )}

        {/* PayPal */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-md bg-sky-100 text-sky-700 flex items-center justify-center">
                <CreditCard className="h-5 w-5" />
              </div>
              <div>
                <p className="font-semibold">PayPal</p>
                <p className="text-xs text-slate-500">Richiede Client ID + Secret da PayPal Developer Dashboard.</p>
              </div>
            </div>
            <Switch data-testid="paypal-toggle" checked={form.paypal_enabled} onCheckedChange={(v) => update({ paypal_enabled: v })} />
          </div>
          {form.paypal_enabled && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
              <div className="col-span-2">
                <Label>Client ID</Label>
                <Input data-testid="paypal-client-id" value={form.paypal_client_id || ""} onChange={(e) => update({ paypal_client_id: e.target.value })} placeholder="AeF1abc...XYZ" />
              </div>
              <div className="col-span-2">
                <Label>Secret</Label>
                <Input data-testid="paypal-secret" type="password" value={form.paypal_secret || ""} onChange={(e) => update({ paypal_secret: e.target.value })} placeholder="EHabc...XYZ123" />
              </div>
              <div>
                <Label>Modalità</Label>
                <Select value={form.paypal_mode || "sandbox"} onValueChange={(v) => update({ paypal_mode: v })}>
                  <SelectTrigger data-testid="paypal-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sandbox">Sandbox (test)</SelectItem>
                    <SelectItem value="live">Live (produzione)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2 bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-900 flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <div>
                  <p>Crea le credenziali su <a className="underline" target="_blank" rel="noopener noreferrer" href="https://developer.paypal.com/dashboard/applications/">developer.paypal.com</a>.</p>
                  <p className="mt-1">Modalità Sandbox per test sicuri; passa a Live solo dopo aver verificato il flusso.</p>
                </div>
              </div>
            </div>
          )}
          {form.paypal_enabled && (
            <ClientLabelEditor
              method="paypal"
              label={form.paypal_label}
              description={form.paypal_description}
              defaultLabel="PayPal"
              defaultDescription="Paga rapidamente con il tuo conto PayPal."
              onChange={update}
            />
          )}
        </div>
      </div>

      <div className="flex justify-end">
        <Button data-testid="save-payment-settings" onClick={save} disabled={saving || !ibanValid} className="bg-[#0F172A] hover:bg-[#1E293B]">
          <Save className="h-4 w-4 mr-2" /> {saving ? "Salvataggio…" : "Salva configurazione"}
        </Button>
      </div>
    </div>
  );
}
